import { useId, useLayoutEffect, useRef, useState } from "react";
import { Download } from "lucide-react";

const formats = [
  { value: "html", label: "Rich text HTML", help: "Download this script with its authored colors, word sizes and text formatting." },
  { value: "txt", label: "Plain text", help: "Download only the script text. Formatting, tags and production notes are omitted." },
  { value: "json", label: "Editable JSON", help: "Download the complete script with formatting, title, tags and production notes for later import." },
] as const;

export function ScriptExportMenu({ title, onExport }: {
  title: string;
  onExport: (format: "html" | "txt" | "json") => void;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const initialItem = useRef(0);
  const [open, setOpen] = useState(false);
  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus({ preventScroll: true });
  };
  useLayoutEffect(() => {
    const node = panel.current;
    if (!node || !open) return;
    node.showPopover();
    const position = () => {
      const anchor = trigger.current?.getBoundingClientRect();
      if (!anchor) return;
      const gutter = 8;
      node.style.maxHeight = `${Math.max(80, window.innerHeight - gutter * 2)}px`;
      const height = node.offsetHeight;
      const top = anchor.bottom + 6 + height <= window.innerHeight - gutter
        ? anchor.bottom + 6 : anchor.top - height - 6;
      node.style.left = `${Math.max(gutter, Math.min(anchor.left, window.innerWidth - node.offsetWidth - gutter))}px`;
      node.style.top = `${Math.max(gutter, Math.min(top, window.innerHeight - height - gutter))}px`;
    };
    position();
    const items = [...node.querySelectorAll<HTMLButtonElement>("button")];
    items.forEach((item, index) => { item.tabIndex = index === initialItem.current ? 0 : -1; });
    items[initialItem.current]?.focus({ preventScroll: true });
    const observer = new ResizeObserver(position);
    observer.observe(node);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open]);
  return <>
    <button
      ref={trigger}
      type="button"
      className={`icon-button ${open ? "selected" : ""}`}
      aria-label={`Export ${title || "Untitled script"}`}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? id : undefined}
      data-help="Export this library script as formatted HTML, plain text or editable JSON while keeping the current script on your displays."
      onClick={() => {
        initialItem.current = 0;
        if (open) close();
        else setOpen(true);
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          event.stopPropagation();
          initialItem.current = event.key === "ArrowUp" ? formats.length - 1 : 0;
          setOpen(true);
        }
        if (event.key === "Escape" && open) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
      }}
    ><Download size={13} /></button>
    {open && <div
      id={id}
      ref={panel}
      popover="auto"
      role="menu"
      aria-label="Export script"
      className="script-export-menu"
      onToggle={(event) => { if (event.newState === "closed") close(false); }}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget) && event.relatedTarget !== trigger.current) close(false);
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Escape") { event.preventDefault(); close(); return; }
        if (event.key === "Tab" && event.shiftKey) { event.preventDefault(); close(); return; }
        const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("[role='menuitem']")];
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        const target = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
          : event.key === "ArrowDown" ? (index + 1) % items.length
          : event.key === "ArrowUp" ? (index + items.length - 1) % items.length : -1;
        if (target >= 0) {
          event.preventDefault();
          items.forEach((item, i) => { item.tabIndex = i === target ? 0 : -1; });
          items[target].focus();
        }
      }}
    >
      {formats.map((format, index) => <button
        key={format.value}
        type="button"
        role="menuitem"
        tabIndex={index === 0 ? 0 : -1}
        data-help={format.help}
        onClick={() => { close(); onExport(format.value); }}
      >{format.label}</button>)}
    </div>}
  </>;
}
